/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { ProductCategory } from '@toucan/types/interfaces';

import { MediaQuery } from '@toucan/helpers/media-queries';

import { WrappedLink } from '@toucan/client/global/nextjs';
import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
    ConstrainedWrapperSize,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { LinkColor, LinkSize } from '@toucan/client/shared/components/link';
import {
    ScrollableCarousel,
    ScrollableCarouselButtonSize,
} from '@toucan/client/shared/components/scrollable-carousel';
import { useMediaQuery } from '@toucan/client/shared/hooks/use-media-query';

import Curve from './curve.svg';
import { styles } from './hero-banner.styles';

export enum HeroBannerVariant {
    LIGHT = 'light',
    DARK = 'dark',
    GRAPHIC = 'GRAPHIC',
    CATEGORY = 'CATEGORY',
    NO_BACKGROUND = 'no-background',
}

export type HeroBannerProps = {
    /**
     * Heading show in the hero
     */
    heading?: string | ReactNode;

    /**
     * Image to show in the hero
     */
    image?: ReactNode;

    /**
     * Modifies the text colour
     * 1. LIGHT
     * 2. DARK
     * 3. GRAPHIC
     * 4. CATEGORY
     * 5. NO_BACKGROUND
     *
     * @default LIGHT
     */
    variant?: HeroBannerVariant;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * The subheading
     */
    subHeading?: string | ReactNode;

    /**
     * The content for the header
     */
    content?: string | ReactNode;

    /**
     * A breadcrumb to show
     */
    breadcrumb?: ReactNode;

    /**
     * Subcategory links to be displayed in the hero image
     */
    subcategories?: ProductCategory[];

    /**
     * The slug of the page we are rendering on
     */
    slug?: string;

    /**
     * What size should the content be
     * @default ConstrainedWrapperSize.S_1440
     */
    contentSize?: ConstrainedWrapperSize;

    /**
     * What size should the heading be
     * @default HeadingSize.H1
     */
    headingSize?: HeadingSize.H1 | HeadingSize.H2;

    children?: ReactNode;
};

const StyledHeroBanner = styled('section').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const HeroBanner: FunctionComponent<HeroBannerProps> = ({
    heading,
    image,
    subHeading,
    content,
    breadcrumb,
    subcategories = [],
    variant = HeroBannerVariant.DARK,
    contentSize = ConstrainedWrapperSize.S_1440,
    headingSize = HeadingSize.H1,
    children,
    ...otherProps
}: HeroBannerProps) => {
    const isMobile = useMediaQuery(MediaQuery.MOBILE);

    /**
     * Renders the image for the banner
     */
    function renderImage() {
        switch (variant) {
            case HeroBannerVariant.NO_BACKGROUND:
                return null;

            case HeroBannerVariant.CATEGORY:
                return null;

            default:
                return image;
        }
    }

    const subcategoryLinks = subcategories.map(
        ({ title, templatePath, url }: ProductCategory, index) => {
            return (
                <WrappedLink
                    linkColor={LinkColor.NAVIGATION}
                    size={LinkSize.SMALL}
                    href={templatePath}
                    as={url}
                    key={index}
                >
                    {title}
                </WrappedLink>
            );
        }
    );

    const subcategoryEl = (
        <ScrollableCarousel
            items={subcategoryLinks}
            buttonSize={ScrollableCarouselButtonSize.SMALL}
            removeButtonTabIndex={true}
            showButtons={true}
            className="hero-banner-subcategories"
        />
    );

    return (
        <StyledHeroBanner variant={variant} {...otherProps}>
            {renderImage()}

            {breadcrumb && (
                <div className="hero-banner-breadcrumbs">{breadcrumb}</div>
            )}

            <ConstrainedWrapper
                className="hero-banner-content-wrapper"
                size={contentSize}
                type={ConstrainedWrapperType.PADDING}
                gutter={ConstrainedWrapperGutter.LARGE_GUTTER}
            >
                {heading && (
                    <Heading className="hero-banner-heading" size={headingSize}>
                        {heading}
                    </Heading>
                )}
                {subHeading && (
                    <Heading
                        className="hero-banner-subheading"
                        size={HeadingSize.H2}
                    >
                        {subHeading}
                    </Heading>
                )}
                {content && (
                    <div className="hero-banner-content">{content}</div>
                )}
                {!isMobile && subcategories.length ? subcategoryEl : null}
                {children}
            </ConstrainedWrapper>

            {isMobile && subcategories.length ? subcategoryEl : null}

            {variant !== HeroBannerVariant.NO_BACKGROUND && (
                <div className="hero-banner-shape-container">
                    <Curve className="hero-banner-shape" />
                </div>
            )}
        </StyledHeroBanner>
    );
};
